/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useRef } from 'react';
import { AdvancedFilter as AdvancedFilterKit } from '@veeam/components';
import deepcopy from 'deepcopy';

import type { AdvancedFilterProps } from '@veeam/components';

import { useGridApi } from '../Grid/hooks';

import type { AdvancedFilterValue } from './interfaces';

export const AdvancedFilter: React.FC<AdvancedFilterProps<AdvancedFilterValue>> = (props) => {
    const gridApi = useGridApi();
    const { value, options } = props;
    const usedFields = useRef<string[]>([...new Set(findUsedFields().map(x => x.name))]);

    function findUsedFields() {
        const result: AdvancedFilterValue[] = [];

        options.cols.forEach(col => col.groups.forEach(group => group.items.forEach(item => result.push(item.value))));

        return result;
    }

    function transformValue(): typeof value {
        const result = [];
        const usedFieldsIds = findUsedFields().map(x => x.id);

        for (const key of Object.keys(gridApi.filters)) {
            const filter = gridApi.filters[key];

            if (filter instanceof Array && filter.length > 0 && usedFields.current.includes(key)) {
                filter.forEach(value => result.push({ name: key, id: value }));
            }
        }

        return result.filter(x => usedFieldsIds.includes(x.id));
    }

    if (gridApi) {
        return (
            <AdvancedFilterKit
                value={transformValue()}
                onChange={(value) => {
                    const newValue = deepcopy(value).reduce((accumulator, currentValue) => {
                        const key = currentValue.name;

                        if (!accumulator[key]) {
                            accumulator[key] = [];
                        }

                        accumulator[key].push(currentValue.id);

                        return accumulator;
                    }, {});

                    for (const field of usedFields.current) {
                        if (!newValue[field]) {
                            newValue[field] = [];
                        }
                    }

                    gridApi.applyFilters(Object.keys(newValue).reduce((accumulator, currentValue) => {
                        accumulator[currentValue] = newValue[currentValue];

                        return accumulator;
                    }, {}));
                }}
                {...props}
            />
        );
    } else {
        return <AdvancedFilterKit {...props} />;
    }
};
